import { FC, useEffect } from 'react'
import { CircularProgress, Box, Button, Typography } from '@mui/material'
import { PersonSearchRounded } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { isEmbeddedContext, useAnyCompassEmbed } from '@/features/anycompass'
import { getYakurekiList } from '@/shared/apis/yakureki'
import { localStorageWrapper } from '@/shared/helpers/localStorage'

const LAST_ACCESSED_KEY = 'lastAccessedRequestId'

/**
 * ランディングページ.
 * ルート (/) アクセス時に最後に見ていた YakurekiEvent (= 薬歴) もしくは最新の薬歴の SOAP 画面へ redirect する.
 *
 * log/33 P3 で patient.id ベースから yakurekiId ベースに切替.
 * 既存 IndexPage (`/soap/:requestId`) の `requestId` は YakurekiEvent.id として MeetingBlock に渡される.
 *
 * 埋め込み (AnyCOMPASS iframe) では「今どの患者か」は親が決める. ここに来た時点で URL は
 * 患者を失っているので、親から deeplink で受けた患者 (?patient_name=)、それも無ければ
 * INIT の knjName へ戻す. どちらも無ければ何も開かない. 最新受付や直近アクセスを開くと
 * 患者取り違えそのものになる (log/193).
 */
export const LandingOrRedirect: FC = () => {
  const navigate = useNavigate()
  // 必ず isEmbeddedContext() (同期判定) を使う. EmbedContext の isEmbedded (pin 済み) は
  // 確定まで最大 6 秒かかり、その窓を素通りする (helpers/embedContext.ts).
  const embedded = isEmbeddedContext()
  const { deeplinkPatientName, patientContext } = useAnyCompassEmbed()

  // 戻す患者は deeplink (SSoT) を第一に、無ければ INIT の knjName で補う.
  // deeplink はフルロード (403 ゲートからのログインし直し・エラー画面の復帰ボタン) で
  // クエリごと失われるが、READY→INIT は route に依らず再実行されるので、親が INIT を
  // 再送する世代ならここで患者へ戻れる. 両方無ければ何も開かない (下の unresolved 表示).
  const embedPatientName =
    deeplinkPatientName ?? patientContext?.knjName ?? null

  const { data, isLoading } = useQuery({
    queryKey: ['yakurekiList', 'landing'],
    queryFn: async () => {
      // back の一覧 API はユーザー単位で 10 秒キャッシュされる. landing はこの一覧から
      // 送り先を決めるので、 破棄で消えたフリー録音がキャッシュに残っていると 404 → '/' →
      // 同じ event へ、 をキャッシュが切れるまで往復する (useGoneRedirect の取り直しも
      // キャッシュを読む). 着地の判断に古い一覧は使えないため、 ここだけキャッシュを迂回する.
      const res = await getYakurekiList({ page_size: 50, force_refresh: true })
      return res.data
    },
    // 埋め込みでは一覧から患者を選ばないので、取得自体が不要.
    enabled: !embedded,
  })

  useEffect(() => {
    if (embedded) {
      if (embedPatientName) {
        // auto_start は載せない. 復帰のたびに不意の録音開始をしないため (quick-record の方針).
        // quick-record は patient_name 経路では 0 件・失敗時も landing へ戻らず自画面に
        // 留まるので、ここと往復するループにはならない.
        navigate(
          `/yakureki/quick-record?patient_name=${encodeURIComponent(
            embedPatientName
          )}`,
          { replace: true }
        )
      }
      return
    }

    if (isLoading) return

    const list = data?.results ?? []

    if (list.length === 0) {
      // 薬歴 0 件: IndexPage が "new" を受け取って空 main を表示する.
      navigate('/soap/new', { replace: true })
      return
    }

    // 最後にアクセスした薬歴を優先. 存在しない場合は list の先頭 (= 最新) に fallback.
    // この値を書くのは旧画面だけ (apps/legacy の usePatientPageState / pages/index)。新画面では
    // 旧画面の頃の値しか残らず、下の「頼まれた処方を除く」線はかけていない (自動で録音は
    // 始まらないので取り違えにはならない。back log/250 work-log/13 の既知の制約)
    const lastAccessedId = localStorageWrapper.getItem(LAST_ACCESSED_KEY)
    let targetId: string | undefined
    if (lastAccessedId) {
      const exists = list.find((y) => y.id === lastAccessedId)
      if (exists) targetId = lastAccessedId
    }
    // 遠隔の店では、頼まれた処方 (他店のもの) は最新の fallback にしない (back log/250)
    if (!targetId) {
      targetId = list.find((y) => y.remote?.direction !== 'incoming')?.id
    }

    // 一覧が頼まれた処方だけなら、自店の 0 件と同じ扱い (開く先が無いままだと読み込み中で止まる)
    navigate(targetId ? `/soap/${targetId}` : '/soap/new', { replace: true })
  }, [data, navigate, isLoading, embedded, embedPatientName])

  if (embedded && !embedPatientName) {
    // 「薬歴がありません」とは言わない. 薬歴はホスト側の資産で、所在を誤って伝えることに
    // なる (quick-record の終端表示と同じ作法). この画面は Layout の外でサイドバーも無く
    // 遷移先を持たないので、案内するのは実際の回復手段 (親から開き直す) と再読込だけ.
    // 再読込は飾りではない: 親が INIT を再送する世代では READY→INIT で knjName が
    // 届き直し、上の effect が患者へ戻す. INIT の届く前にこの画面が一瞬出る場合も
    // 同じ経路で自動的に抜ける.
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="background.paper"
        gap={2}
        px={3}
        textAlign="center"
      >
        <PersonSearchRounded sx={{ fontSize: 48, color: 'text.disabled' }} />
        <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
          患者を特定できませんでした
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          AnyCOMPASS から患者を開き直してください。
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => window.location.reload()}
        >
          再読込
        </Button>
      </Box>
    )
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
    >
      <CircularProgress size={60} thickness={4} />
      <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
        読み込み中...
      </Typography>
    </Box>
  )
}
